import React, { useEffect, useRef, useState, useImperativeHandle } from 'react'
import mapboxgl from 'mapbox-gl'
import 'mapbox-gl/dist/mapbox-gl.css'
import { MapLayerControl, MAP_LAYERS } from './MapLayerControl'
import { RoutePopupCard } from './RoutePopupCard'
import { ClubMarkers } from './ClubMarkers'
import { RouteMarkers } from './RouteMarkers'
import { EmbedConfig } from '../../services/firebase/embeds'

// Temporary type definition to avoid circular dependency
type RouteModalData = any

interface ClubMarker {
  id: string
  name: string
  description: string
  location: string
  coordinates: { lat: number; lng: number }
  memberCount: number
  routeCount: number
  logoURL?: string
  website?: string
  membershipType?: 'open' | 'approval' | 'invite'
  isPublic?: boolean
  clubTypes?: string[]
}

interface MapViewProps {
  className?: string
  mapCenter?: [number, number] | null
  mapZoom?: number | null
  onMapUpdate?: () => void
  selectedClub?: string
  onClubChange?: (clubId: string) => void
  sidebarOpen?: boolean
  clubMarkers?: ClubMarker[]
  showSampleRoutes?: boolean
  onClubMarkerClick?: (club: ClubMarker) => void
  selectedClubId?: string | null
  publishedRoutes?: any[]
  onRouteMarkerClick?: (route: any) => void
  selectedRouteCard?: RouteModalData | null
  onRouteCardSelect?: (route: RouteModalData | null) => void
  userMemberships?: Array<{ clubId: string; status: string; role: string }>
  showMapControls?: boolean // New prop to control 3D and layer buttons visibility
  currentLayer?: string
  onLayerChange?: (layerId: string) => void
  onMapReady?: (map: mapboxgl.Map) => void // Callback when map is ready
  alwaysRenderRoutes?: boolean // New prop for embed mode
  showMarkers?: boolean // New prop to show/hide markers when routes are permanent
  onLazyLoadRoute?: (routeId: string) => Promise<any> // New prop for lazy loading route data
  disableRouteClustering?: boolean // New prop to disable clustering (useful for embeds)
  onPhotoLoad?: (routeId: string) => Promise<string[]> // New prop for loading photos
  embedMode?: boolean // New prop to enable photo slider instead of popup card
  embedConfig?: EmbedConfig // New prop to pass embed configuration
  visiblePOICategories?: string[] // New prop for visible POI categories
  onPOISelect?: (poi: any) => void // New prop for POI selection
  disableScrollZoom?: boolean // New prop to disable scroll zoom for embeds
}

export const MapView = React.memo(React.forwardRef<any, MapViewProps>(function MapView({ 
  className = '', 
  mapCenter, 
  mapZoom, 
  onMapUpdate, 
  selectedClub, 
  onClubChange, 
  sidebarOpen, 
  clubMarkers = [],
  showSampleRoutes = false,
  onClubMarkerClick,
  selectedClubId,
  publishedRoutes = [],
  onRouteMarkerClick,
  selectedRouteCard,
  onRouteCardSelect,
  userMemberships = [],
  showMapControls = false, // Default to false - only HomePage should show these controls
  currentLayer: propCurrentLayer,
  onLayerChange: propOnLayerChange,
  onMapReady,
  alwaysRenderRoutes = false,
  showMarkers = true,
  onLazyLoadRoute,
  disableRouteClustering = false,
  onPhotoLoad,
  embedMode = false,
  embedConfig,
  visiblePOICategories,
  onPOISelect,
  disableScrollZoom = false
}: MapViewProps, ref) {
  console.log('🔍 MapView rendered with embedMode:', embedMode)
  const mapContainer = useRef<HTMLDivElement>(null)
  const map = useRef<mapboxgl.Map | null>(null)
  const [mapInstance, setMapInstance] = useState<mapboxgl.Map | null>(null)
  
  // Use prop values if provided, otherwise use internal state
  const [internalCurrentLayer, setInternalCurrentLayer] = useState('outdoors')
  
  const currentLayer = propCurrentLayer !== undefined ? propCurrentLayer : internalCurrentLayer
  
  // Use prop-based state if provided, otherwise fall back to local state
  const [localSelectedRoute, setLocalSelectedRoute] = useState<RouteModalData | null>(null)
  const selectedRoute = selectedRouteCard !== undefined ? selectedRouteCard : localSelectedRoute
  const setSelectedRoute = onRouteCardSelect || setLocalSelectedRoute
  
  // Expose map methods via ref
  useImperativeHandle(ref, () => ({
    fitBounds: (bounds: [[number, number], [number, number]], options?: mapboxgl.FitBoundsOptions) => {
      if (map.current) {
        map.current.fitBounds(bounds, options)
      }
    }
  }), [])

  useEffect(() => {
    if (map.current) return // Initialize map only once
    
    if (!mapContainer.current) return

    mapboxgl.accessToken = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN

    // Get the initial style based on currentLayer prop
    const layer = MAP_LAYERS.find(l => l.id === currentLayer) || MAP_LAYERS.find(l => l.id === 'outdoors')
    console.log('🗺️ MapView initializing with layer:', currentLayer, 'style:', layer!.style)
    
    // Initialize map with appropriate style (handle Google Maps layers)
    let initialStyle: string | mapboxgl.Style
    if (layer!.style.startsWith('google-')) {
      initialStyle = {
        version: 8,
        sources: {
          'google': {
            type: 'raster',
            tiles: layer!.style === 'google-satellite' 
              ? ['https://mt0.google.com/vt/lyrs=s&x={x}&y={y}&z={z}',
                 'https://mt1.google.com/vt/lyrs=s&x={x}&y={y}&z={z}',
                 'https://mt2.google.com/vt/lyrs=s&x={x}&y={y}&z={z}',
                 'https://mt3.google.com/vt/lyrs=s&x={x}&y={y}&z={z}']
              : layer!.style === 'google-hybrid'
              ? ['https://mt0.google.com/vt/lyrs=y&x={x}&y={y}&z={z}',
                 'https://mt1.google.com/vt/lyrs=y&x={x}&y={y}&z={z}',
                 'https://mt2.google.com/vt/lyrs=y&x={x}&y={y}&z={z}',
                 'https://mt3.google.com/vt/lyrs=y&x={x}&y={y}&z={z}']
              : ['https://mt0.google.com/vt/lyrs=p&x={x}&y={y}&z={z}',
                 'https://mt1.google.com/vt/lyrs=p&x={x}&y={y}&z={z}',
                 'https://mt2.google.com/vt/lyrs=p&x={x}&y={y}&z={z}',
                 'https://mt3.google.com/vt/lyrs=p&x={x}&y={y}&z={z}'],
            tileSize: 256,
            attribution: '© Google Maps'
          }
        },
        layers: [{
          id: 'google',
          type: 'raster',
          source: 'google',
          paint: {}
        }],
        glyphs: "mapbox://fonts/mapbox/{fontstack}/{range}.pbf"
      } as mapboxgl.Style
    } else {
      initialStyle = layer!.style
    }
    
    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: initialStyle,
      center: [143.5, -39.5], // Centered further west to better show Victoria and Tasmania
      zoom: 6.5, // Zoomed in to show VIC and TAS nicely
      pitch: 0,
      bearing: 0,
      scrollZoom: !disableScrollZoom // Disable scroll zoom if prop is true
    })
    
    // Add embed class to map container if in embed mode
    if (embedMode) {
      console.log('🎯 MapView: embedMode is true, adding mapbox-embed-mode class')
      mapContainer.current.classList.add('mapbox-embed-mode')
    }
    
    // Set map instance immediately so RouteMarkers can render
    setMapInstance(map.current)

    // Add navigation controls with custom positioning
    const navControl = new mapboxgl.NavigationControl()
    map.current.addControl(navControl, 'top-right')
    
    // Force transparent styles for embed mode after Mapbox renders
    if (embedMode) {
      // Try multiple times to ensure styles are applied
      const applyStyles = () => {
        const navControls = mapContainer.current.querySelectorAll('.mapboxgl-ctrl-group')
        console.log('🎨 Applying transparent styles to', navControls.length, 'navigation controls')
        navControls.forEach((control: any) => {
          control.style.setProperty('background', 'rgba(31, 41, 55, 0.75)', 'important') // gray-800/75
          control.style.setProperty('background-color', 'rgba(31, 41, 55, 0.75)', 'important') // gray-800/75
          control.style.setProperty('backdrop-filter', 'blur(8px)', 'important')
          control.style.setProperty('-webkit-backdrop-filter', 'blur(8px)', 'important')
          control.style.setProperty('border', 'none', 'important')
        })
      }
      
      // Apply immediately and after delays - multiple times to ensure it sticks
      applyStyles() // Apply immediately
      setTimeout(applyStyles, 100)
      setTimeout(applyStyles, 500)
      setTimeout(applyStyles, 1000)
      setTimeout(applyStyles, 2000) // Extra application after 2 seconds
      setTimeout(applyStyles, 3000) // And after 3 seconds to be sure
    }
    
    // Apply dark mode styles to navigation controls
    const mapContainerElement = map.current.getContainer()
    
    // Apply the SAME styles to ALL maps - matching HomePage
    console.log('🎯 MapView: Applying navigation control styles')
    const style = document.createElement('style')
    style.textContent = `
      /* Dark mode styling for Mapbox navigation controls - SAME FOR ALL MAPS */
      ${!embedMode ? '.mapboxgl-ctrl-group' : '.embed-never-match'} {
        background-color: rgba(31, 41, 55, 0.6) !important; /* gray-800/60 */
        backdrop-filter: blur(4px) !important;
        -webkit-backdrop-filter: blur(4px) !important;
        border: none !important;
        box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05) !important;
      }
      
      .mapboxgl-ctrl-group button {
        background-color: transparent !important;
        border-color: rgba(55, 65, 81, 0.3) !important; /* gray-700 with lower opacity */
        width: 32px !important;
        height: 32px !important;
      }
      
      .mapboxgl-ctrl-group button:hover {
        background-color: rgba(31, 41, 55, 0.8) !important; /* gray-800/80 on hover */
      }
      
      .mapboxgl-ctrl-group button:focus {
        box-shadow: none !important;
        outline: none !important;
      }
      
      /* Icon colors */
      .mapboxgl-ctrl-icon {
        filter: invert(1) !important; /* Make icons white */
      }
      
      /* Compass specific styling */
      .mapboxgl-ctrl-compass .mapboxgl-ctrl-icon {
        background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg width='29' height='29' viewBox='0 0 29 29' xmlns='http://www.w3.org/2000/svg' fill='%23ffffff'%3E%3Cpath d='M10.5 14l4-8 4 8z'/%3E%3Cpath d='M10.5 16l4 8 4-8z' fill='%23ccc'/%3E%3C/svg%3E") !important;
        filter: none !important;
      }
    `
    
    mapContainerElement.appendChild(style)

    // Add terrain source and setup
    map.current.on('load', () => {
      if (!map.current) return
      
      // Adjust navigation control position to be below header
      const navContainer = map.current.getContainer().querySelector('.mapboxgl-ctrl-top-right') as HTMLElement
      if (navContainer) {
        navContainer.style.top = '60px' // Moved down 55px total from original position
      }
      
      // Don't apply conflicting styles - the embed mode styles are already applied above

      // Add terrain source for 3D (always load it)
      if (!map.current.getSource('mapbox-dem')) {
        map.current.addSource('mapbox-dem', {
          type: 'raster-dem',
          url: 'mapbox://mapbox.mapbox-terrain-dem-v1',
          tileSize: 512,
          maxzoom: 14
        })
      }
      
      // Always enable terrain for better visuals
      map.current.setTerrain({ source: 'mapbox-dem', exaggeration: 1.5 })
      
      // Always set a slight pitch for 3D effect
      map.current.setPitch(20)
      
      // Call onMapReady callback
      if (map.current && onMapReady) {
        onMapReady(map.current)
      }
    })

    return () => {
      if (map.current) {
        map.current.remove()
        map.current = null
        setMapInstance(null)
      }
    }
  }, [embedMode])

  // Handle map center and zoom changes from parent component
  useEffect(() => {
    if (map.current && mapCenter && mapZoom) {
      map.current.easeTo({
        center: mapCenter,
        zoom: mapZoom,
        duration: 1500 // Smooth 1.5s animation
      })
      
      // Reset the state after applying the change
      if (onMapUpdate) {
        setTimeout(() => {
          onMapUpdate()
        }, 1600) // Slightly after animation completes
      }
    }
  }, [mapCenter, mapZoom, onMapUpdate])

  // Handle sidebar resize - trigger map resize when sidebar opens/closes
  useEffect(() => {
    if (map.current) {
      // Use a small delay to allow the CSS transition to complete
      const timer = setTimeout(() => {
        map.current?.resize()
      }, 350) // Slightly longer than the 300ms sidebar transition
      
      return () => clearTimeout(timer)
    }
  }, [sidebarOpen])

  // Handle external layer changes via props
  useEffect(() => {
    if (map.current && propCurrentLayer !== undefined) {
      const layer = MAP_LAYERS.find(l => l.id === propCurrentLayer)
      if (layer) {
        console.log('🗺️ Changing map style to:', propCurrentLayer, 'URL:', layer.style)
        
        // Handle Google Maps layers specially
        if (layer.style.startsWith('google-')) {
          map.current.setStyle({
            version: 8,
            sources: {
              'google': {
                type: 'raster',
                tiles: layer.style === 'google-satellite' 
                  ? ['https://mt0.google.com/vt/lyrs=s&x={x}&y={y}&z={z}',
                     'https://mt1.google.com/vt/lyrs=s&x={x}&y={y}&z={z}',
                     'https://mt2.google.com/vt/lyrs=s&x={x}&y={y}&z={z}',
                     'https://mt3.google.com/vt/lyrs=s&x={x}&y={y}&z={z}']
                  : layer.style === 'google-hybrid'
                  ? ['https://mt0.google.com/vt/lyrs=y&x={x}&y={y}&z={z}',
                     'https://mt1.google.com/vt/lyrs=y&x={x}&y={y}&z={z}',
                     'https://mt2.google.com/vt/lyrs=y&x={x}&y={y}&z={z}',
                     'https://mt3.google.com/vt/lyrs=y&x={x}&y={y}&z={z}']
                  : ['https://mt0.google.com/vt/lyrs=p&x={x}&y={y}&z={z}',
                     'https://mt1.google.com/vt/lyrs=p&x={x}&y={y}&z={z}',
                     'https://mt2.google.com/vt/lyrs=p&x={x}&y={y}&z={z}',
                     'https://mt3.google.com/vt/lyrs=p&x={x}&y={y}&z={z}'],
                tileSize: 256,
                attribution: '© Google Maps'
              }
            },
            layers: [{
              id: 'google',
              type: 'raster',
              source: 'google',
              paint: {}
            }],
            glyphs: "mapbox://fonts/mapbox/{fontstack}/{range}.pbf"
          })
        } else {
          // For regular Mapbox styles
          map.current.setStyle(layer.style)
        }
        
        // Re-add terrain source and routes after style loads
        map.current.once('style.load', () => {
          if (!map.current) return

          // Re-add terrain source for 3D
          if (!map.current.getSource('mapbox-dem')) {
            map.current.addSource('mapbox-dem', {
              type: 'raster-dem',
              url: 'mapbox://mapbox.mapbox-terrain-dem-v1',
              tileSize: 512,
              maxzoom: 14
            })
          }

          // Always enable terrain for better visuals
          map.current.setTerrain({ source: 'mapbox-dem', exaggeration: 1.5 })
          
          // Always set pitch for 3D effect
          map.current.setPitch(20)
        })
      }
    }
  }, [propCurrentLayer])


  // Handle layer changes
  const handleLayerChange = (layerId: string) => {
    const layer = MAP_LAYERS.find(l => l.id === layerId)
    if (layer && map.current) {
      if (propOnLayerChange) {
        propOnLayerChange(layerId)
      } else {
        setInternalCurrentLayer(layerId)
      }
      
      // Handle Google Maps layers specially
      if (layer.style.startsWith('google-')) {
        map.current.setStyle({
          version: 8,
          sources: {
            'google': {
              type: 'raster',
              tiles: layer.style === 'google-satellite' 
                ? ['https://mt0.google.com/vt/lyrs=s&x={x}&y={y}&z={z}',
                   'https://mt1.google.com/vt/lyrs=s&x={x}&y={y}&z={z}',
                   'https://mt2.google.com/vt/lyrs=s&x={x}&y={y}&z={z}',
                   'https://mt3.google.com/vt/lyrs=s&x={x}&y={y}&z={z}']
                : layer.style === 'google-hybrid'
                ? ['https://mt0.google.com/vt/lyrs=y&x={x}&y={y}&z={z}',
                   'https://mt1.google.com/vt/lyrs=y&x={x}&y={y}&z={z}',
                   'https://mt2.google.com/vt/lyrs=y&x={x}&y={y}&z={z}',
                   'https://mt3.google.com/vt/lyrs=y&x={x}&y={y}&z={z}']
                : ['https://mt0.google.com/vt/lyrs=p&x={x}&y={y}&z={z}',
                   'https://mt1.google.com/vt/lyrs=p&x={x}&y={y}&z={z}',
                   'https://mt2.google.com/vt/lyrs=p&x={x}&y={y}&z={z}',
                   'https://mt3.google.com/vt/lyrs=p&x={x}&y={y}&z={z}'],
              tileSize: 256,
              attribution: '© Google Maps'
            }
          },
          layers: [{
            id: 'google',
            type: 'raster',
            source: 'google',
            paint: {}
          }],
          glyphs: "mapbox://fonts/mapbox/{fontstack}/{range}.pbf"
        })
        
        // For Google styles, the style.load event might not fire the same way
        // So we manually trigger the route re-rendering after a delay
        // Google Hybrid needs extra time as it loads both satellite and road layers
        const delay = layer.style === 'google-hybrid' ? 500 : 200
        
        setTimeout(() => {
          if (!map.current) return
          console.log(`🗺️ Google ${layer.style} loaded, manually triggering style.load event`)
          // Manually fire the style.load event for listeners
          map.current.fire('style.load')
          
          // Also check if the map is loaded and fire again if needed
          if (!map.current.loaded()) {
            console.log('🗺️ Map not yet loaded, waiting...')
            map.current.once('idle', () => {
              console.log('🗺️ Map idle, firing style.load event again')
              map.current?.fire('style.load')
            })
          }
          
          // For Google Hybrid, also listen for the render event as a final fallback
          if (layer.style === 'google-hybrid') {
            map.current.once('render', () => {
              setTimeout(() => {
                console.log('🗺️ Google Hybrid rendered, ensuring routes are displayed')
                map.current?.fire('style.load')
              }, 100)
            })
          }
        }, delay)
      } else {
        // For regular Mapbox styles
        map.current.setStyle(layer.style)
      }
      
      // Re-add terrain source and routes after style loads
      // This will now work for both Mapbox and Google styles
      map.current.once('style.load', () => {
        if (!map.current) return

        // Re-add terrain source for 3D (only for Mapbox styles, not Google)
        if (!layer.style.startsWith('google-') && !map.current.getSource('mapbox-dem')) {
          map.current.addSource('mapbox-dem', {
            type: 'raster-dem',
            url: 'mapbox://mapbox.mapbox-terrain-dem-v1',
            tileSize: 512,
            maxzoom: 14
          })
          
          // Always enable terrain for better visuals
          map.current.setTerrain({ source: 'mapbox-dem', exaggeration: 1.5 })
        }
        
        // Always set pitch for 3D effect
        map.current.setPitch(20)
      })
    }
  }


  return (
    <div className={`relative ${className}`}>
      <div ref={mapContainer} className="w-full h-full" />
      
      {/* Club Markers */}
      <ClubMarkers 
        map={map.current}
        clubMarkers={clubMarkers}
        selectedClubId={selectedClubId}
        onClubMarkerClick={onClubMarkerClick}
      />
      
      {/* Route Markers - only render after map is initialized */}
      {mapInstance && (
        <RouteMarkers
          map={mapInstance}
          publishedRoutes={publishedRoutes}
          onRouteMarkerClick={onRouteMarkerClick}
          onRouteSelect={setSelectedRoute}
          selectedRoute={selectedRoute}
          alwaysRenderRoutes={alwaysRenderRoutes}
          showMarkers={showMarkers}
          onLazyLoadRoute={onLazyLoadRoute}
          disableRouteClustering={disableRouteClustering}
          onPhotoLoad={onPhotoLoad}
          embedMode={embedMode}
          embedConfig={embedConfig}
          visiblePOICategories={visiblePOICategories}
          onPOISelect={onPOISelect}
        />
      )}
      
      

      {/* Route Detail Card */}
      <RoutePopupCard 
        route={selectedRoute}
        onClose={() => setSelectedRoute(null)}
        onViewFullRoute={onRouteMarkerClick}
        userMemberships={userMemberships}
      />
    </div>
  )
}))